import { css } from '@emotion/core';

import { codeFontFamily, colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

const space050 = token('space.050', '4px');
const space100 = token('space.100', '8px');
const space150 = token('space.150', '12px');
const space200 = token('space.200', '16px');
const space300 = token('space.300', '24px');

const codeStyles = css({
  background: token('elevation.surface.sunken', colors.N20),
  borderRadius: token('border.radius', '3px'),
  fontFamily: codeFontFamily(),
  fontSize: '12px',
  overflowX: 'auto',
});

// These rules are based on the CSS that Bitbucket Core applies to markup files
// see: media/less/components/wiki-content.less
// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
export const markupContentStyles = css`
  hr {
    margin: ${space200} 0;
    border: 0;
    border-bottom: 1px solid ${token('color.border', colors.N40)};
  }

  p,
  li {
    word-wrap: break-word;
  }

  li > p {
    margin-top: ${space100};
  }

  li:first-child > p {
    margin-top: 0;
  }

  ol.loweralpha {
    list-style-type: lower-alpha;
  }

  ol.upperalpha {
    list-style-type: upper-alpha;
  }

  ol.lowerroman {
    list-style-type: lower-roman;
  }

  ol.upperroman {
    list-style-type: upper-roman;
  }

  .codehilite {
    margin-top: ${space100};

    &:first-child {
      margin-top: 0;
    }
  }

  blockquote {
    border-left: 1px solid ${token('color.border', colors.N50)};
    color: ${token('color.text.subtlest', colors.N100)};
    padding: ${space100} ${space300};
    margin: ${space100} 0 0;
  }

  pre {
    padding: ${space200} ${space100};
    word-wrap: normal;
    ${codeStyles};
  }

  img {
    max-width: 100%;

    &.emoji {
      /* Styles copied from https://github.com/twitter/twemoji#inline-styles
         with some changes to make the emojis bigger to align with pull request comments. */
      height: 1.2em;

      /* Pull them down slightly to center them better with the text */
      vertical-align: -0.1em;

      /* Make sure whether there is an emoji in the line or not does not affect the line height */
      margin-top: -0.1em;
      margin-bottom: -0.1em;

      /* Some horizontal space between multiple emojis */
      margin-right: 0.05em;
      margin-left: 0.1em;
    }
  }

  table {
    margin-top: ${space100};

    > thead > tr > th,
    > tbody > tr > th,
    > thead > tr > td,
    > tbody > tr > td,
    > tfoot > tr > td {
      padding: ${space100} ${space150};

      &[align='center'] {
        text-align: center;
      }

      &[align='right'] {
        text-align: right;
      }
    }

    &.footnote {
      margin-top: inherit;
      width: auto;

      &:first-of-type {
        margin-top: ${space100};
      }

      td {
        padding: 0 ${space050};
      }
    }

    table {
      margin: 0;
    }

    > caption {
      background: ${token('color.background.input', colors.N20)};
      border-bottom: 1px solid ${token('color.border', colors.N40)};
      caption-side: top;
      color: ${token('color.text.subtlest', colors.N300)};
      padding: ${space100} ${space150};
      text-align: left;
    }
  }

  code {
    max-width: 100%;
    padding: 2px 4px;
    ${codeStyles};
  }

  pre > code {
    white-space: pre;
  }

  > ul {
    list-style-type: disc;
  }

  > ul > li > ul {
    list-style-type: circle;
  }

  ul > li > ul {
    margin-top: 0;
  }

  > ul > li > ul > li > ul {
    list-style-type: square;
  }

  .toc ul {
    margin-top: ${space150};
  }

  /*
    RST admonitions
    <div>s are necessary to prevent styles from being accidentally applied to inline elements (eg in code blocks)
  */
  div.attention,
  div.caution,
  div.danger,
  div.error,
  div.hint,
  div.important,
  div.note,
  div.tip,
  div.warning,
  div.admonition {
    background: ${token('elevation.surface', colors.N0)};
    border: 1px solid ${token('color.border', colors.N40)};
    border-radius: ${token('border.radius', '3px')};
    margin: ${space100} 0 0;
    padding: ${space100};
    position: relative;
  }

  div.attention,
  div.caution,
  div.warning,
  div.admonition {
    background: ${token('color.background.warning', colors.Y50)};
    border-color: ${token('color.border.warning', colors.Y75)};
  }

  .danger,
  .error {
    color: ${token('color.text.danger', colors.R300)};
  }

  div.danger,
  div.error {
    background: ${token('elevation.surface', colors.N0)};
    border-color: ${token('color.border.danger', colors.R300)};
  }

  .admonition-title {
    font-weight: bold;
  }

  /*
    AK's CSS reset auto-inserts quotation marks, which also have alignment and whitespace issues
    We want to override this pattern and remove the quotation marks (which the AK Editor also does)
    https://jdog.jira-dev.com/browse/ADG-2205
    https://ecosystem.atlassian.net/browse/DS-3817
  */
  blockquote::before,
  q::before,
  blockquote::after,
  q::after {
    content: '';
  }
`;

/* prettier-ignore */
// These rules come directly from the CSS that Bitbucket Core applies to code blocks in markup files
// see: node_modules/pygments-css/trac.css
/* stylelint-disable */
// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage, @atlaskit/design-system/no-css-tagged-template-expression
export const syntaxHighlightStyles = css`
  --codehilite-keyword-color: ${token('color.text.accent.red', '#815b3a')};
  --codehilite-number-color: ${token('color.text.accent.blue', '#009999')};
  --codehilite-string-color: ${token('color.text.accent.orange', '#bb8844')};
  --codehilite-comment-color: ${token('color.text.subtlest', '#999988')};
  --codehilite-error-color: ${token('color.border.danger', '#f00')};

  .codehilite {
    background-color: var(--bb-background-code, #fff);

    .hll {
      background-color: var(--bb-background-code, #fff);
      color: ${token('color.text', '#000')};
    }

    .c {
      /* Comment */
      color: var(--codehilite-comment-color);
      font-style: italic;
    }

    .err {
      /* Error */
      color: var(--codehilite-error-color);
      background-color: ${token('color.background.accent.red.subtlest', '#e3d2d2')};
    }

    .k {
      /* Keyword */
      color: var(--codehilite-keyword-color);
      font-weight: bold;
    }

    .o {
      /* Operator */
      color: ${token('color.text', '#000')};
      font-weight: bold;
    }

    .cm {
      /* Comment.Multiline */
      color: var(--codehilite-comment-color);
      font-style: italic;
    }

    .cp {
      /* Comment.Preproc */
      color: var(--codehilite-string-color);
      font-weight: bold;
    }

    .c1 {
      /* Comment.Single */
      color: var(--codehilite-comment-color);
      font-style: italic;
    }

    .cs {
      /* Comment.Special */
      color: ${token('color.text.subtlest', '#999999')};
      font-weight: bold;
      font-style: italic;
    }

    .gd {
      /* Generic.Deleted */
      color: ${token('color.text', '#000000')};
      background-color: ${token('color.background.accent.red.subtlest', '#ffdddd')};
    }

    .ge {
      /* Generic.Emph */
      font-style: italic;
    }

    .gr {
      /* Generic.Error */
      color: var(--codehilite-error-color);
    }

    .gh {
      /* Generic.Heading */
      color: ${token('color.background.accent.gray.bolder', '#4a6785')};
      font-weight: bold;
    }

    .gi {
      /* Generic.Inserted */
      color: ${token('color.text', '#000000')};
      background-color: ${token('color.background.accent.green.subtlest', '#ddffdd')};
    }

    .go {
      /* Generic.Output */
      color: ${token('color.text.subtlest', '#888888')};
    }

    .gp {
      /* Generic.Prompt */
      color: ${token('color.text.subtle', '#555555')};
    }

    .gs {
      /* Generic.Strong */
      font-weight: bold;
    }

    .gu {
      /* Generic.Subheading */
      color: ${token('color.background.accent.gray.bolder', '#aaaaaa')};
    }

    .gt {
      /* Generic.Traceback */
      color: var(--codehilite-error-color);
    }

    .kc {
      /* Keyword.Constant */
      color: var(--codehilite-keyword-color);
      font-weight: bold;
    }

    .kd {
      /* Keyword.Declaration */
      color: var(--codehilite-keyword-color);
      font-weight: bold;
    }

    .kn {
      /* Keyword.Namespace */
      color: var(--codehilite-keyword-color);
      font-weight: bold;
    }

    .kp {
      /* Keyword.Pseudo */
      color: var(--codehilite-keyword-color);
      font-weight: bold;
    }

    .kr {
      /* Keyword.Reserved */
      color: var(--codehilite-keyword-color);
      font-weight: bold;
    }

    .kt {
      /* Keyword.Type */
      color: ${token('color.text.accent.blue.bolder', '#445588')};
      font-weight: bold;
    }

    .m {
      /* Literal.Number */
      color: var(--codehilite-number-color);
    }

    .s {
      /* Literal.String */
      color: var(--codehilite-string-color);
    }

    .na {
      /* Name.Attribute */
      color: ${token('color.text.success', '#008080')};
    }

    .nb {
      /* Name.Builtin */
      color: ${token('color.text.accent.blue.bolder', '#999999')};
    }

    .nc {
      /* Name.Class */
      color: ${token('color.text.accent.blue', '#445588')};
      font-weight: bold;
    }

    .no {
      /* Name.Constant */
      color: ${token('color.text.accent.gray', '#008080')};
    }

    .ni {
      /* Name.Entity */
      color: ${token('color.text.accent.blue.bolder', '#800080')};
    }

    .ne {
      /* Name.Exception */
      color: var(--codehilite-error-color);
      font-weight: bold;
    }

    .nf {
      /* Name.Function */
      color: ${token('color.text.accent.orange', '#990000')};
      font-weight: bold;
    }

    .nn {
      /* Name.Namespace */
      color: ${token('color.text.subtle', '#555555')};
    }

    .nt {
      /* Name.Tag */
      color: ${token('color.text.brand', '#000080')};
    }

    .nv {
      /* Name.Variable */
      color: ${token('color.text.accent.gray', '#008080')};
    }

    .ow {
      /* Operator.Word */
      font-weight: bold;
    }

    .w {
      /* Text.Whitespace */
      color: ${token('color.text.disabled', '#bbbbbb')};
    }

    .mf {
      /* Literal.Number.Float */
      color: var(--codehilite-number-color);
    }

    .mh {
      /* Literal.Number.Hex */
      color: var(--codehilite-number-color);
    }

    .mi {
      /* Literal.Number.Integer */
      color: var(--codehilite-number-color);
    }

    .mo {
      /* Literal.Number.Oct */
      color: var(--codehilite-number-color);
    }

    .sb {
      /* Literal.String.Backtick */
      color: var(--codehilite-string-color);
    }

    .sc {
      /* Literal.String.Char */
      color: var(--codehilite-string-color);
    }

    .sd {
      /* Literal.String.Doc */
      color: var(--codehilite-string-color);
    }

    .s2 {
      /* Literal.String.Double */
      color: var(--codehilite-string-color);
    }

    .se {
      /* Literal.String.Escape */
      color: var(--codehilite-string-color);
    }

    .sh {
      /* Literal.String.Heredoc */
      color: var(--codehilite-string-color);
    }

    .si {
      /* Literal.String.Interpol */
      color: var(--codehilite-string-color);
    }

    .sx {
      /* Literal.String.Other */
      color: var(--codehilite-string-color);
    }

    .sr {
      /* Literal.String.Regex */
      color: ${token('color.text.accent.yellow', '#808000')};
    }

    .s1 {
      /* Literal.String.Single */
      color: var(--codehilite-string-color);
    }

    .ss {
      /* Literal.String.Symbol */
      color: var(--codehilite-string-color);
    }

    .bp {
      /* Name.Builtin.Pseudo */
      color: ${token('color.text.accent.blue.bolder', '#999999')};
    }

    .vc {
      /* Name.Variable.Class */
      color: ${token('color.text.accent.gray', '#008080')};
    }

    .vg {
      /* Name.Variable.Global */
      color: ${token('color.text.accent.gray', '#008080')};
    }

    .vi {
      /* Name.Variable.Instance */
      color: ${token('color.text.accent.gray', '#008080')};
    }

    .il {
      /* Literal.Number.Integer.Long */
      color: var(--codehilite-number-color);
    }
  }
`;
/* stylelint-enable */
